import Tippy from '@tippy.js/react';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import TenancyOptionGroup from '~components/booking-process/select-bed/tenancy-option-group';
import { academicYearInfosToTenancyOptionsList } from '~components/datepicker/tenancy-option-helpers';
import Typography from '~components/typography';
import { SP_QueryRoomTypeAvailability_getRoomTypeAvailability } from '~graphql/__generated__/SP_QueryRoomTypeAvailability';
import { useTranslation } from '~i18n';
import { IconJumpDown } from '~svg-components';
import styles from './tenancy-option-select.module.scss';

interface IDate {
  startDate: Date | undefined;
  endDate: Date | undefined;
}
interface ITenancyOptionSelectProps {
  academicYearInfos?: SP_QueryRoomTypeAvailability_getRoomTypeAvailability['academicYearInfos'];
  tenancyOptionId: string;
  tenancyDate: IDate;
  setAcademicYearId: (value: string) => void;
  setTenancyDate: (value: IDate) => void;
  setTenancyOptionId: (value: string) => void;
  setTenancyOptionRateType: (value: string) => void;
  setTenancyOptionLengthType: (value: string) => void;
}

const TenancyOptionSelect: React.FC<ITenancyOptionSelectProps> = ({
  tenancyDate,
  setTenancyDate,
  tenancyOptionId,
  setAcademicYearId,
  setTenancyOptionId,
  setTenancyOptionRateType,
  setTenancyOptionLengthType,
  academicYearInfos,
}) => {
  const [t] = useTranslation('student_portal');
  const [groupVisible, setGroupVisible] = useState(false);

  const tenancyOptionsList = useMemo(
    () => academicYearInfosToTenancyOptionsList(academicYearInfos),
    [academicYearInfos]
  );

  const selectTenancy = useMemo(() => {
    let tenancyOption = null;
    for (let item of tenancyOptionsList) {
      const tenancyOptionItem = item.tenancyOptions.find(({ id }) => id === tenancyOptionId);
      if (tenancyOptionItem) {
        tenancyOption = tenancyOptionItem;
      }
    }
    return tenancyOption;
  }, [tenancyOptionId, tenancyOptionsList]);

  useEffect(() => {
    if (!tenancyDate.startDate && selectTenancy) {
      setTenancyDate({
        startDate: new Date(selectTenancy?.startDate),
        endDate: new Date(selectTenancy?.endDate),
      });
    }
  }, [selectTenancy, setTenancyDate, tenancyDate.startDate]);
  return (
    <div className={styles.root}>
      <Typography component="div" variant="body3" fontWeight={600}>
        {t('select_bed.tenancy_length')}
      </Typography>
      <Tippy
        onShow={() => {
          setGroupVisible(true);
        }}
        onHide={() => {
          setGroupVisible(false);
        }}
        hideOnClick={'toggle'}
        visible={groupVisible}
        maxWidth={580}
        offset={'[0, 4]'}
        trigger="click"
        theme={'white'}
        appendTo="parent"
        className={styles.tippy}
        content={
          <TenancyOptionGroup
            className={styles.group}
            tenancyOptionList={tenancyOptionsList}
            selectedValue={tenancyOptionId}
            setFlexibleMobileVisible={setGroupVisible}
            onSelect={({
              tenancyOptionId,
              tenancyOptionStartDate,
              tenancyOptionEndDate,
              academicYearId,
              tenancyOptionRateType,
              tenancyLengthOption,
            }) => {
              setGroupVisible(false);
              setTenancyOptionId(tenancyOptionId);
              setTenancyDate({
                startDate: tenancyOptionStartDate,
                endDate: tenancyOptionEndDate,
              });
              if (tenancyOptionRateType) setTenancyOptionRateType(tenancyOptionRateType);
              if (academicYearId) setAcademicYearId(academicYearId);
              if (tenancyLengthOption) setTenancyOptionLengthType(tenancyLengthOption);
            }}
          />
        }
        placement="bottom"
      >
        <div
          className={styles.container}
          onClick={() => {
            setGroupVisible(!groupVisible);
          }}
        >
          <div className={styles.title}>
            <div className={styles.tenancyOption}>
              {selectTenancy?.name && (
                <Typography component="div" variant="body3" className={styles.tenancyOptionName}>
                  {selectTenancy?.name}
                </Typography>
              )}

              {!selectTenancy?.name && (
                <Typography variant="body3" color="var(--color-purple_3)">
                  {t('select_length_of_stay')}
                </Typography>
              )}
            </div>

            <IconJumpDown
              width="8"
              height="6"
              fill="var(--color-grey_99)"
              className={classNames(styles.icon, {
                [styles.iconActive]: groupVisible,
              })}
            />
          </div>
        </div>
      </Tippy>
    </div>
  );
};

export default TenancyOptionSelect;
